/**
 * CONNOX GMBH CONFIDENTIAL
 * ________________________
 *
 * Copyright (c) 2005 - 2022 Connox GmbH, All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Connox GmbH and its suppliers, if any. The intellectual and
 * technical concepts contained herein are proprietary to Connox GmbH and
 * its suppliers and may be covered by German Laws and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * strictly forbidden unless prior written permission is obtained from
 * Connox GmbH.
 */

import {ICartData} from "../cart";
import {ICartMessage} from "../cart";
import {CartRequestHandler} from "../cart/requestHandlers/CartRequestHandler";
import {EventEmitter} from "../events/EventEmitter";
import {IEventEmitter} from "../events/IEventEmitter";
import {IAddressResponseData} from "./AddressResponseData";
import {ICheckoutData} from "./CheckoutData";
import {IPaymentData} from "./Payment/PaymentData";
import {PaymentFactory} from "./Payment/PaymentFactory";
import {
  CheckoutRequestHandler,
  ICheckoutPutParams,
  IGetCheckoutShippingOptionsData,
  IPaymentTransaction,
} from "./requestHandlers/CheckoutRequestHandler";
import {ShippingFactory} from "./Shipping/ShippingFactory";

export class Checkout {
  private checkoutData: ICheckoutData;
  private readonly shopId: number;
  private readonly eventEmitter: IEventEmitter<ICheckoutData>;

  constructor(shopId: number, checkoutData: ICheckoutData) {
    this.shopId = shopId;
    this.checkoutData = checkoutData;
    this.eventEmitter = new EventEmitter<ICheckoutData>();

    this.eventEmitter.on((payload) => {
      if (payload === this.checkoutData) {
        return;
      }
      this.checkoutData = payload;
    });

    this.getAvailableShippingOptions = this.getAvailableShippingOptions.bind(this) as () => Promise<
      IGetCheckoutShippingOptionsData[]
    >;
    this.getAvailablePaymentOptions = this.getAvailablePaymentOptions.bind(this) as () => Promise<IPaymentData[]>;
    this.purchase = this.purchase.bind(this) as () => Promise<void>;
    this.get = this.get.bind(this) as () => Promise<void>;
  }

  public get hasNewsletter(): boolean | undefined {
    return this.checkoutData.hasNewsletter;
  }

  public get customerRemark(): string | null {
    return this.checkoutData.customerRemark;
  }

  public get checkoutId(): string {
    return this.checkoutData.checkoutId;
  }

  public get orderId(): number | undefined {
    return this.checkoutData.orderId;
  }

  public setCheckoutData(checkoutData: ICheckoutData): void {
    this.checkoutData = checkoutData;
  }

  public get cartData(): ICartData {
    return this.checkoutData.cart;
  }

  public get paymentId(): number | null {
    return this.checkoutData.paymentId;
  }

  public get paymentTransaction(): IPaymentTransaction | null {
    return this.checkoutData.paymentTransaction;
  }

  public get isCompleted(): boolean {
    return this.checkoutData.isCompleted;
  }

  public get isValid(): boolean {
    return this.checkoutData.isValid;
  }

  public get logisticsTypeId(): number | null {
    return this.checkoutData.logisticsTypeId;
  }

  public get messages(): ICartMessage[] {
    return this.checkoutData.messages;
  }

  public get deliveryAddress(): IAddressResponseData | null {
    return this.checkoutData.address.delivery;
  }

  public get invoiceAddress(): IAddressResponseData | null {
    return this.checkoutData.address.invoice;
  }

  public get favouredDateShipping(): string | null {
    return this.checkoutData.favouredDateShipping;
  }

  public get isPartDeliveryAllowed(): boolean {
    return this.checkoutData.isPartDeliveryAllowed;
  }

  public get isGuest(): boolean {
    return this.checkoutData.isGuest;
  }

  public get emitter(): IEventEmitter<ICheckoutData> {
    return this.eventEmitter;
  }

  public async get(this: Checkout): Promise<void> {
    this.checkoutData = await CheckoutRequestHandler.get(this.shopId, this.checkoutId);
    this.eventEmitter.emit(this.checkoutData);
  }

  public async put(this: Checkout, data: ICheckoutPutParams): Promise<void> {
    if (data.cart) {
      await CartRequestHandler.putParams(this.shopId, this.cartData.cartId, data.cart);
      delete data.cart;
    }
    this.checkoutData = await CheckoutRequestHandler.putParams(this.shopId, this.checkoutId, data);
    this.eventEmitter.emit(this.checkoutData);
  }

  public async getAvailableShippingOptions(this: Checkout): Promise<IGetCheckoutShippingOptionsData[]> {
    return ShippingFactory.getShippingOptions(this.shopId, this.checkoutId);
  }

  public async purchase(this: Checkout, hasNewsletter?: boolean): Promise<void> {
    await this.put({buy: true, hasNewsletter});
  }

  public async getAvailablePaymentOptions(this: Checkout): Promise<IPaymentData[]> {
    return PaymentFactory.getPaymentMethods(this.shopId, this.checkoutId);
  }
}
